import { LocalStorageWorker } from "../utils/local-storage-helper";
import { joinPath } from "../utils/join-path";
import { stringFormat } from "../utils/string-format";
import { SvgType } from "./svg-type";
import { SvgMetadata } from "./svg-metadata";
import { ICON_CDN_URL, ICON_VERSION } from "../constants/global-constants";

/**
 * Use this loader to receive SVGs like icons or pictograms from the eon-ui-icons CDN.
 * Requests are cached in JS and SVG content/metadata is stored in the LocalStorage of the browser.
 * This way, every icon will only get loaded exactly once per session.
 */
export class SvgLoader {
  // TODO: Move this to a config file
  // private VERSION = "x.x.x";
  // to Update the eon-ui-CDN-Version you must change the meta-tag <meta name="eon-ui-icons-version" content="x.x.x"/>
  // AND line 44, inside the catch-block to define the backup-version, if the website dont present the meta-tag.
  // to update inside the Storybook go to .storybook/preview-head.html!
  private VERSION = this.getSvgVersion();
  private SVG_CDN = `${ICON_CDN_URL}/${this.VERSION}/`;
  private METADATA_URL = "metadata/{0}-sizes.json";

  /**
   * Map consists of the url and the promise to the URL
   */
  private static requestsCache = new Map<string, Promise<any>>();
  private localStorageWorker: LocalStorageWorker;

  constructor() {
    this.localStorageWorker = new LocalStorageWorker();
  }

  /** this function returns the Version of the CDN Version
   * Either by meta tag or hard coded
   * This is the required tag: <meta name="eon-ui-icons-version" content="x.x.x"/>
   * @private
   */

  private getSvgVersion(): string {
    let svgVersion;
    let metaTag = document.querySelector('meta[name="eon-ui-icons-version"]') !== null;
    try {
      if (metaTag) {
        svgVersion = document.querySelector('meta[name="eon-ui-icons-version"]').getAttribute("content");
      } else {
        throw new Error("Meta-tag not found");
      }
    } catch (e) {
      svgVersion = ICON_VERSION;
    }
    return svgVersion;
  }

  /**
   * Creates or returns a cached request for the given SVG.
   * When this promise is resolved, you can safely access the svg content using the synchronous method getSvg().
   * @param svgName The name of the svg without file extension. E.g. 'add'
   * @param svgType The type of svg to request
   */
  requestSvg(svgName: string, svgType: SvgType): Promise<any> {
    const url = this.getSvgUrl(svgName, svgType);
    const svgKey = this.getSvgKey(svgName, svgType);

    // see if we already have a request for this url
    let request = SvgLoader.requestsCache.get(url);
    if (!request) {
      request = fetch(url).then(async (response: Response) => {
        if (!response.ok) {
          this.localStorageWorker.remove(svgKey);
          throw new Error(`Unable to fetch SVG ${svgType} '${svgName}'.`);
        }
        const svgContent = await response.text();
        this.localStorageWorker.add(svgKey, svgContent);
      });
      // cache the request
      SvgLoader.requestsCache.set(url, request);
    }
    return request;
  }

  /**
   * Creates or returns a cached request for metadata of the given SVG type.
   * When this promise is resolved, you can safely access the metadata using the synchronous methods getSvgMetadata() and getAllSvgNames().
   * @param svgType The type of icon to request
   */
  requestSvgSizes(svgType: SvgType): Promise<any> {
    const url = this.SVG_CDN + stringFormat(this.METADATA_URL, svgType.toString());
    const sizesKey = this.getSizesKey(svgType);

    // see if we already have a request for this url
    let request = SvgLoader.requestsCache.get(url);

    if (!request) {
      request = fetch(url).then(async (response: Response) => {
        if (!response.ok) {
          this.localStorageWorker.remove(sizesKey);
          throw new Error(`Unable to fetch sizes metadata for SVG type '${svgType}'.`);
        }
        const sizes = await response.text();
        this.localStorageWorker.add(sizesKey, sizes);
      });
    }
    return request;
  }

  /**
   * Returns the SVG content for the given SVG as string. This is the <svg> tag and can be used in JSX directly.
   * @param svgName The name of the svg without file extension. E.g. 'add'
   * @param svgType The type of svg to request
   */
  getSvg(svgName: string, svgType: SvgType): string {
    const svgKey = this.getSvgKey(svgName, svgType);
    return this.localStorageWorker.get(svgKey);
  }

  /**
   * Returns the metadata for the given SVG type.
   * @param svgName The name of the svg without file extension. E.g. 'add'
   * @param svgType The type of svg to request
   */
  getSvgMetadata(svgName: string, svgType: SvgType): SvgMetadata {
    const sizesKey = this.getSizesKey(svgType);
    const sizesStr: string = this.localStorageWorker.get(sizesKey);
    if (!sizesStr) {
      return null;
    }
    const sizesParsed = JSON.parse(sizesStr);
    const requestedIconSize = sizesParsed[svgName];
    let iconSize: SvgMetadata = null;
    if (requestedIconSize != null) {
      iconSize = {
        width: parseInt(requestedIconSize.width, 10),
        height: parseInt(requestedIconSize.height, 10)
      };
    }
    return iconSize;
  }

  /**
   * Returns the names of all available SVGs of the given type.
   * @param svgType The type of svg to request
   */
  getAllSvgNames(svgType: SvgType): string[] {
    const sizesKey = this.getSizesKey(svgType);
    const sizesStr: string = this.localStorageWorker.get(sizesKey);
    if (!sizesStr) {
      return null;
    }
    const sizesParsed = JSON.parse(sizesStr);
    return [...Object.keys(sizesParsed)];
  }

  private getSvgUrl(svgName: string, svgType: SvgType) {
    const iconNameWithoutExtension = (svgName = svgName.substr(0, svgName.lastIndexOf(".")) || svgName);
    const url = joinPath(this.SVG_CDN, `${svgType.toString()}s`, `${iconNameWithoutExtension}.svg`);
    return url;
  }

  /**
   * Returns the generated SvgKey. The SvgKey contains Information of: name, time and CDN Version.
   * @param svgName The name of the svg without file extension. E.g. 'add'
   * @param svgType The type of svg to request
   */
  private getSvgKey(svgName: string, svgType: SvgType) {
    return `${svgType.toString()}-${this.VERSION}-${svgName}`;
  }

  private getSizesKey(svgType: SvgType) {
    return `${svgType.toString()}-sizes-${this.VERSION}`;
  }
}
